import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PauseCircleRoundedIcon from "@mui/icons-material/PauseCircleRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { planBillingApi } from "../../api/planBilling";

const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#DB492C",
  orangeSoft: "#FFF2EE",
  dark: "#19242F",
  page: "#F6F7F9",
  border: "rgba(25,36,47,0.10)",
  muted: "rgba(25,36,47,0.62)",
};

const PANEL_SX = {
  borderRadius: 4,
  border: `1px solid ${BRAND.border}`,
  bgcolor: "#FFFFFF",
  boxShadow: "0 16px 42px rgba(25,36,47,0.055)",
};

const PLAN_FEATURES = [
  ["landing", "Landing"],
  ["products", "Productos y servicios"],
  ["orders", "Pedidos"],
  ["payments", "Cobros online"],
  ["invoicing", "Sistema interno"],
  ["stats", "Estadísticas"],
  ["webpush", "Notificaciones Web Push"],
];

function money(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "ARS" ? 0 : 2,
  }).format(Number(value || 0));
}

function dateInput(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

function dateLabel(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(
    parsed,
  );
}

function statusMeta(status) {
  const normalized = String(status || "").toLowerCase();
  const map = {
    trial: { label: "Prueba", color: "warning" },
    pending: { label: "Pendiente", color: "warning" },
    active: { label: "Activa", color: "success" },
    past_due: { label: "Pago atrasado", color: "error" },
    paused: { label: "Pausada", color: "error" },
    cancelled: { label: "Cancelada", color: "default" },
    exempt: { label: "Bonificada", color: "success" },
    authorized: { label: "Autorizada", color: "success" },
    manual_pending: { label: "Pendiente manual", color: "warning" },
    rejected: { label: "Rechazada", color: "error" },
  };
  return (
    map[normalized] || {
      label: normalized ? normalized.replaceAll("_", " ") : "Sin estado",
      color: "default",
    }
  );
}

function MetricCard({ icon, label, value, helper, tone = "default" }) {
  const tones = {
    default: {
      background: "#FFFFFF",
      iconBg: "#F0F2F5",
      iconColor: BRAND.dark,
    },
    primary: {
      background: BRAND.orangeSoft,
      iconBg: "#FFFFFF",
      iconColor: BRAND.orange,
    },
    warning: { background: "#FFF9EE", iconBg: "#FFFFFF", iconColor: "#B26A00" },
    danger: { background: "#FFF5F4", iconBg: "#FFFFFF", iconColor: "#D14343" },
  };
  const current = tones[tone] || tones.default;

  return (
    <Card
      elevation={0}
      sx={{
        ...PANEL_SX,
        height: "100%",
        boxShadow: "none",
        background: current.background,
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            variant="rounded"
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              bgcolor: current.iconBg,
              color: current.iconColor,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: BRAND.muted,
                fontWeight: 900,
                letterSpacing: ".05em",
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mt: 0.2,
                color: BRAND.dark,
                fontWeight: 950,
                lineHeight: 1.2,
              }}
            >
              {value}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.7, color: BRAND.muted }}>
              {helper}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SectionHeading({ eyebrow, title, description, action }) {
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ md: "flex-start" }}
      spacing={2}
    >
      <Box>
        {eyebrow ? (
          <Typography
            variant="overline"
            sx={{
              color: BRAND.orange,
              fontWeight: 900,
              letterSpacing: ".12em",
            }}
          >
            {eyebrow}
          </Typography>
        ) : null}
        <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 950 }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" sx={{ mt: 0.55, color: BRAND.muted }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
  );
}

function PlanEditor({ plan, saving, onSave }) {
  const [form, setForm] = React.useState({
    name: plan.name || "",
    description: plan.description || "",
    monthlyPrice: plan.monthlyPrice || 0,
    currency: plan.currency || "ARS",
    recommended: Boolean(plan.recommended),
    active: plan.active !== false,
    features: (plan.features || []).join("\n"),
    modules: { ...(plan.modules || {}) },
  });

  React.useEffect(() => {
    setForm({
      name: plan.name || "",
      description: plan.description || "",
      monthlyPrice: plan.monthlyPrice || 0,
      currency: plan.currency || "ARS",
      recommended: Boolean(plan.recommended),
      active: plan.active !== false,
      features: (plan.features || []).join("\n"),
      modules: { ...(plan.modules || {}) },
    });
  }, [plan]);

  const enabledCount = PLAN_FEATURES.filter(
    ([key]) => form.modules?.[key],
  ).length;

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 4,
        border: form.recommended
          ? `2px solid ${BRAND.orange}`
          : `1px solid ${BRAND.border}`,
        boxShadow: form.recommended
          ? "0 18px 42px rgba(243,90,58,.13)"
          : "0 12px 32px rgba(25,36,47,.045)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.4,
          bgcolor: form.recommended ? BRAND.orangeSoft : BRAND.page,
          borderBottom: `1px solid ${BRAND.border}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1.5}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: BRAND.muted,
                fontWeight: 900,
                letterSpacing: ".08em",
              }}
            >
              CÓDIGO {String(plan.code || "").toUpperCase()}
            </Typography>
            <Typography
              variant="h5"
              sx={{ mt: 0.3, color: BRAND.dark, fontWeight: 950 }}
            >
              {form.name || "Plan sin nombre"}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={0.7}
            flexWrap="wrap"
            justifyContent="flex-end"
            useFlexGap
          >
            {form.recommended ? (
              <Chip
                size="small"
                label="Recomendado"
                sx={{
                  bgcolor: "#FFFFFF",
                  color: BRAND.orange,
                  fontWeight: 900,
                }}
              />
            ) : null}
            <Chip
              size="small"
              label={form.active ? "Visible" : "Oculto"}
              color={form.active ? "success" : "default"}
              sx={{ fontWeight: 850 }}
            />
          </Stack>
        </Stack>
      </Box>

      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Stack spacing={2.25}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                color: BRAND.dark,
                fontWeight: 950,
                letterSpacing: "-.04em",
              }}
            >
              {money(form.monthlyPrice, form.currency)}
            </Typography>
            <Typography variant="caption" sx={{ color: BRAND.muted }}>
              Precio mensual publicado
            </Typography>
          </Box>

          <Divider />

          <TextField
            label="Nombre comercial"
            size="small"
            value={form.name}
            onChange={(event) =>
              setForm((current) => ({ ...current, name: event.target.value }))
            }
            fullWidth
          />
          <TextField
            label="Descripción"
            size="small"
            multiline
            minRows={2}
            value={form.description}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              label="Precio mensual"
              size="small"
              type="number"
              value={form.monthlyPrice}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  monthlyPrice: event.target.value,
                }))
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
            />
            <TextField
              select
              label="Moneda"
              size="small"
              value={form.currency}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  currency: event.target.value,
                }))
              }
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="ARS">ARS</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="CLP">CLP</MenuItem>
            </TextField>
          </Stack>

          <TextField
            label="Beneficios visibles"
            helperText="Ingresá un beneficio por línea."
            size="small"
            multiline
            minRows={4}
            value={form.features}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                features: event.target.value,
              }))
            }
            fullWidth
          />

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: BRAND.page,
              border: `1px solid ${BRAND.border}`,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography sx={{ color: BRAND.dark, fontWeight: 900 }}>
                  Funcionalidades incluidas
                </Typography>
                <Typography variant="caption" sx={{ color: BRAND.muted }}>
                  {enabledCount} de {PLAN_FEATURES.length} áreas habilitadas
                </Typography>
              </Box>
              <TuneRoundedIcon sx={{ color: BRAND.orange }} />
            </Stack>
            <Grid container spacing={0.4} sx={{ mt: 1 }}>
              {PLAN_FEATURES.map(([key, label]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={Boolean(form.modules?.[key])}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            modules: {
                              ...current.modules,
                              [key]: event.target.checked,
                            },
                          }))
                        }
                      />
                    }
                    label={<Typography variant="caption">{label}</Typography>}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={
                <Switch
                  checked={form.recommended}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      recommended: event.target.checked,
                    }))
                  }
                />
              }
              label="Recomendado"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      active: event.target.checked,
                    }))
                  }
                />
              }
              label="Visible para clientes"
            />
          </Stack>

          <Button
            disabled={saving}
            onClick={() =>
              onSave(plan.code, {
                ...form,
                monthlyPrice: Number(form.monthlyPrice || 0),
                features: form.features
                  .split("\n")
                  .map((item) => item.trim())
                  .filter(Boolean),
              })
            }
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveRoundedIcon />
              )
            }
            variant="contained"
            sx={{
              minHeight: 44,
              bgcolor: BRAND.orange,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 900,
              boxShadow: "0 10px 24px rgba(243,90,58,.24)",
              "&:hover": { bgcolor: BRAND.orangeDark },
            }}
          >
            Guardar plan
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ProjectRow({ project, plans, saving, onSave }) {
  const summary = project.billingSummary || {};
  const [form, setForm] = React.useState({
    planCode: summary.planCode || project.plan || "catalogo",
    billingStatus: summary.status || "trial",
    projectStatus: project.status || "active",
    currentPeriodEnd: dateInput(
      summary.currentPeriodEnd || summary.nextPaymentDate,
    ),
  });

  React.useEffect(() => {
    setForm({
      planCode: summary.planCode || project.plan || "catalogo",
      billingStatus: summary.status || "trial",
      projectStatus: project.status || "active",
      currentPeriodEnd: dateInput(
        summary.currentPeriodEnd || summary.nextPaymentDate,
      ),
    });
  }, [
    project,
    summary.planCode,
    summary.status,
    summary.currentPeriodEnd,
    summary.nextPaymentDate,
  ]);

  const billingStatus = statusMeta(summary.status);

  return (
    <TableRow hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
      <TableCell sx={{ minWidth: 220, py: 1.7 }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Avatar
            variant="rounded"
            sx={{
              width: 38,
              height: 38,
              bgcolor: BRAND.orangeSoft,
              color: BRAND.orange,
            }}
          >
            <StorefrontRoundedIcon fontSize="small" />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>
              {project.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: "block", color: BRAND.muted }}
            >
              {project.owner?.email || project.slug}
            </Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell sx={{ minWidth: 165 }}>
        <TextField
          select
          size="small"
          value={form.planCode}
          onChange={(event) =>
            setForm((current) => ({ ...current, planCode: event.target.value }))
          }
          fullWidth
        >
          {plans.map((plan) => (
            <MenuItem key={plan.code} value={plan.code}>
              {plan.name}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
        <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>
          {money(summary.monthlyAmount, summary.currency)}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 170 }}>
        <TextField
          select
          size="small"
          value={form.billingStatus}
          onChange={(event) =>
            setForm((current) => ({
              ...current,
              billingStatus: event.target.value,
            }))
          }
          fullWidth
        >
          <MenuItem value="trial">Prueba</MenuItem>
          <MenuItem value="pending">Pendiente</MenuItem>
          <MenuItem value="active">Activa</MenuItem>
          <MenuItem value="past_due">Pago atrasado</MenuItem>
          <MenuItem value="paused">Pausada</MenuItem>
          <MenuItem value="cancelled">Cancelada</MenuItem>
          <MenuItem value="exempt">Bonificada</MenuItem>
        </TextField>
        <Chip
          size="small"
          label={billingStatus.label}
          color={billingStatus.color}
          variant="outlined"
          sx={{ mt: 0.7, height: 21, fontSize: 11, fontWeight: 800 }}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 165 }}>
        <Chip
          size="small"
          label={
            summary.isRecurring
              ? "Mercado Pago"
              : summary.provider === "manual"
                ? "Legado / bonificado"
                : "Sin asociar"
          }
          color={summary.isRecurring ? "success" : "default"}
          sx={{ fontWeight: 800 }}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 170 }}>
        <TextField
          size="small"
          type="date"
          value={form.currentPeriodEnd}
          onChange={(event) =>
            setForm((current) => ({
              ...current,
              currentPeriodEnd: event.target.value,
            }))
          }
          fullWidth
        />
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}>
        <TextField
          select
          size="small"
          value={form.projectStatus}
          onChange={(event) =>
            setForm((current) => ({
              ...current,
              projectStatus: event.target.value,
            }))
          }
          fullWidth
        >
          <MenuItem value="active">Activa</MenuItem>
          <MenuItem value="paused">Pausada</MenuItem>
          <MenuItem value="cancelled">Cancelada</MenuItem>
        </TextField>
      </TableCell>
      <TableCell align="right" sx={{ minWidth: 150 }}>
        <Button
          disabled={saving}
          onClick={() =>
            onSave(project.id || project.slug, {
              ...form,
              currentPeriodEnd: form.currentPeriodEnd || null,
            })
          }
          startIcon={<SaveRoundedIcon />}
          variant="contained"
          sx={{
            bgcolor: BRAND.dark,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 900,
            boxShadow: "none",
            "&:hover": { bgcolor: "#0F171E", boxShadow: "none" },
          }}
        >
          Aplicar
        </Button>
      </TableCell>
    </TableRow>
  );
}

export default function BillingPlansAdminPage() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setData(await planBillingApi.adminOverview());
    } catch (err) {
      setError(err.message || "No se pudo cargar el billing");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const run = async (operation, successMessage) => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await operation();
      setMessage(successMessage);
      await load();
    } catch (err) {
      setError(err.message || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const pending = (data?.subscriptions || []).filter((subscription) =>
    ["pending", "manual_pending"].includes(subscription.status),
  );

  const filteredProjects = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return (data?.projects || []).filter((project) => {
      const summary = project.billingSummary || {};
      const matchesStatus =
        statusFilter === "all" || summary.status === statusFilter;
      const haystack = [
        project.name,
        project.slug,
        project.owner?.email,
        summary.planCode,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesQuery =
        !normalizedQuery || haystack.includes(normalizedQuery);
      return matchesStatus && matchesQuery;
    });
  }, [data?.projects, query, statusFilter]);

  if (loading && !data) {
    return (
      <Box
        sx={{
          minHeight: 440,
          display: "grid",
          placeItems: "center",
          borderRadius: 4,
          bgcolor: BRAND.page,
        }}
      >
        <Stack spacing={1.5} alignItems="center">
          <CircularProgress size={36} sx={{ color: BRAND.orange }} />
          <Typography sx={{ color: BRAND.muted, fontWeight: 800 }}>
            Cargando planes y suscripciones…
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 5,
            p: { xs: 2.5, md: 4 },
            color: "#FFFFFF",
            background:
              "linear-gradient(135deg, #19242F 0%, #253746 58%, #334B5D 100%)",
            boxShadow: "0 22px 55px rgba(25,36,47,0.20)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 290,
              height: 290,
              borderRadius: "50%",
              right: -115,
              top: -145,
              bgcolor: "rgba(243,90,58,0.24)",
            }}
          />
          <Stack
            position="relative"
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
            spacing={3}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: "rgba(255,255,255,.68)",
                  fontWeight: 900,
                  letterSpacing: ".14em",
                }}
              >
                ADMINISTRACIÓN CENTRAL
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  mt: 0.7,
                  fontWeight: 950,
                  fontSize: { xs: "2rem", md: "2.65rem" },
                  letterSpacing: "-.04em",
                }}
              >
                Planes y suscripciones
              </Typography>
              <Typography
                sx={{ mt: 1, color: "rgba(255,255,255,.72)", maxWidth: 760 }}
              >
                Definí precios y funcionalidades, supervisá autorizaciones de
                Mercado Pago y controlá el estado comercial de todos los
                clientes.
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 2.2 }}
              >
                <Chip
                  icon={<WorkspacePremiumRoundedIcon />}
                  label={`${data?.plans?.length || 0} planes configurados`}
                  sx={{
                    bgcolor: "rgba(255,255,255,.12)",
                    color: "#FFFFFF",
                    fontWeight: 850,
                  }}
                />
                <Chip
                  icon={<AutorenewRoundedIcon />}
                  label={`${data?.metrics?.recurring || 0} suscripciones recurrentes`}
                  sx={{
                    bgcolor: "rgba(255,255,255,.12)",
                    color: "#FFFFFF",
                    fontWeight: 850,
                  }}
                />
              </Stack>
            </Box>

            <Button
              onClick={load}
              disabled={loading || saving}
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RefreshRoundedIcon />
                )
              }
              variant="outlined"
              sx={{
                color: "#FFFFFF",
                borderColor: "rgba(255,255,255,.38)",
                borderRadius: 999,
                px: 2.2,
                textTransform: "none",
                fontWeight: 900,
                "&:hover": {
                  borderColor: "#FFFFFF",
                  bgcolor: "rgba(255,255,255,.08)",
                },
              }}
            >
              Actualizar panel
            </Button>
          </Stack>
        </Paper>

        {error ? (
          <Alert
            severity="error"
            onClose={() => setError("")}
            sx={{ borderRadius: 3 }}
          >
            {error}
          </Alert>
        ) : null}
        {message ? (
          <Alert
            severity="success"
            onClose={() => setMessage("")}
            sx={{ borderRadius: 3 }}
          >
            {message}
          </Alert>
        ) : null}

        <Grid container spacing={2.25}>
          <Grid item xs={12} sm={6} xl={3}>
            <MetricCard
              icon={<StorefrontRoundedIcon />}
              label="CLIENTES / PROYECTOS"
              value={data?.metrics?.projects || 0}
              helper="Proyectos registrados dentro de WebProfit."
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={3}>
            <MetricCard
              icon={<TrendingUpRoundedIcon />}
              label="INGRESO MENSUAL"
              value={money(
                data?.metrics?.monthlyRecurring || 0,
                data?.metrics?.revenueCurrency || "ARS",
              )}
              helper="MRR estimado de planes activos y recurrentes."
              tone="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={3}>
            <MetricCard
              icon={<CreditCardRoundedIcon />}
              label="RECURRENTES MP"
              value={data?.metrics?.recurring || 0}
              helper="Suscripciones vinculadas a Mercado Pago."
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={3}>
            <MetricCard
              icon={<PendingActionsRoundedIcon />}
              label="REQUIEREN ATENCIÓN"
              value={
                (data?.metrics?.pending || 0) + (data?.metrics?.paused || 0)
              }
              helper={`${data?.metrics?.pending || 0} pendientes · ${data?.metrics?.paused || 0} pausadas.`}
              tone={
                (data?.metrics?.pending || 0) + (data?.metrics?.paused || 0) > 0
                  ? "warning"
                  : "default"
              }
            />
          </Grid>
        </Grid>

        <Box>
          <SectionHeading
            eyebrow="CATÁLOGO COMERCIAL"
            title="Planes mensuales"
            description="Configurá el precio, la presentación y las funcionalidades incluidas en cada opción."
          />
          <Grid container spacing={2.5} sx={{ mt: 0.25 }}>
            {(data?.plans || []).map((plan) => (
              <Grid item xs={12} md={6} xl={4} key={plan.code}>
                <PlanEditor
                  plan={plan}
                  saving={saving}
                  onSave={(code, payload) =>
                    run(
                      () => planBillingApi.updatePlan(code, payload),
                      `Plan ${code} actualizado.`,
                    )
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Paper elevation={0} sx={{ ...PANEL_SX, p: { xs: 2.5, md: 3.25 } }}>
          <SectionHeading
            eyebrow="AUTORIZACIONES"
            title="Suscripciones pendientes"
            description="Seguimiento de clientes que todavía no completaron la autorización recurrente."
          />
          <TableContainer
            sx={{
              mt: 2.5,
              borderRadius: 3,
              border: `1px solid ${BRAND.border}`,
              overflowX: "auto",
            }}
          >
            <Table sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F7F8FA" }}>
                  {[
                    "Cliente",
                    "Plan",
                    "Modalidad",
                    "Mensualidad",
                    "Estado",
                    "Seguimiento",
                  ].map((label, index) => (
                    <TableCell
                      key={label}
                      align={index === 3 || index === 5 ? "right" : "left"}
                      sx={{
                        color: BRAND.muted,
                        fontSize: 12,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: ".07em",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.map((subscription) => {
                  const meta = statusMeta(subscription.status);
                  return (
                    <TableRow key={subscription.id || subscription._id} hover>
                      <TableCell>
                        <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>
                          {subscription.projectId?.name || "Proyecto"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: BRAND.muted }}
                        >
                          {subscription.requestedBy?.email || "Sin email"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={<WorkspacePremiumRoundedIcon />}
                          label={subscription.requestedPlanCode || "—"}
                          sx={{ fontWeight: 850 }}
                        />
                      </TableCell>
                      <TableCell>
                        {subscription.provider === "mercadopago"
                          ? "Mercado Pago"
                          : "Manual"}
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>
                          {money(subscription.amount, subscription.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={meta.label}
                          color={meta.color}
                          sx={{ fontWeight: 850 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="caption"
                          sx={{ color: BRAND.muted }}
                        >
                          {subscription.provider === "mercadopago"
                            ? "Esperando autorización de tarjeta"
                            : "Registro legado: revisar antes de migrar"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!pending.length ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 6, textAlign: "center" }}>
                      <PendingActionsRoundedIcon
                        sx={{ fontSize: 42, color: "rgba(25,36,47,.24)" }}
                      />
                      <Typography
                        sx={{ mt: 1, color: BRAND.dark, fontWeight: 900 }}
                      >
                        No hay autorizaciones pendientes
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.4, color: BRAND.muted }}
                      >
                        Todas las solicitudes se encuentran conciliadas.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper elevation={0} sx={{ ...PANEL_SX, p: { xs: 2.5, md: 3.25 } }}>
          <SectionHeading
            eyebrow="CARTERA"
            title="Clientes y estado del plan"
            description="Supervisá la suscripción, el próximo cobro y el estado público de cada proyecto."
            action={
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                <TextField
                  size="small"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Buscar cliente, email o plan"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: { xs: "100%", sm: 260 } }}
                />
                <TextField
                  select
                  size="small"
                  label="Estado"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  sx={{ minWidth: 155 }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="trial">Prueba</MenuItem>
                  <MenuItem value="pending">Pendiente</MenuItem>
                  <MenuItem value="active">Activa</MenuItem>
                  <MenuItem value="past_due">Pago atrasado</MenuItem>
                  <MenuItem value="paused">Pausada</MenuItem>
                  <MenuItem value="cancelled">Cancelada</MenuItem>
                  <MenuItem value="exempt">Bonificada</MenuItem>
                </TextField>
              </Stack>
            }
          />

          <TableContainer
            sx={{
              mt: 2.5,
              borderRadius: 3,
              border: `1px solid ${BRAND.border}`,
              overflowX: "auto",
            }}
          >
            <Table sx={{ minWidth: 1320 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F7F8FA" }}>
                  {[
                    "Cliente",
                    "Plan",
                    "Mensualidad",
                    "Suscripción",
                    "Proveedor",
                    "Próximo cobro",
                    "Página",
                    "Acciones",
                  ].map((label, index) => (
                    <TableCell
                      key={label}
                      align={index === 2 || index === 7 ? "right" : "left"}
                      sx={{
                        color: BRAND.muted,
                        fontSize: 12,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: ".07em",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProjects.map((project) => (
                  <ProjectRow
                    key={project.id || project.slug}
                    project={project}
                    plans={data?.plans || []}
                    saving={saving}
                    onSave={(id, payload) =>
                      run(
                        () => planBillingApi.updateProject(id, payload),
                        "Cliente actualizado.",
                      )
                    }
                  />
                ))}
                {!filteredProjects.length ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 6, textAlign: "center" }}>
                      <SearchRoundedIcon
                        sx={{ fontSize: 42, color: "rgba(25,36,47,.24)" }}
                      />
                      <Typography
                        sx={{ mt: 1, color: BRAND.dark, fontWeight: 900 }}
                      >
                        No encontramos clientes
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.4, color: BRAND.muted }}
                      >
                        Probá con otro término o cambiá el filtro de estado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.3,
            borderRadius: 3,
            border: "1px solid rgba(237,108,2,.18)",
            bgcolor: "rgba(237,108,2,.045)",
          }}
        >
          <Stack direction="row" spacing={1.4} alignItems="flex-start">
            <InfoOutlinedIcon sx={{ mt: 0.2, color: "warning.main" }} />
            <Box>
              <Typography sx={{ color: BRAND.dark, fontWeight: 900 }}>
                Uso de los controles administrativos
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.45, color: BRAND.muted }}>
                Los cambios manuales de estado deben reservarse para
                migraciones, bonificaciones o correcciones excepcionales. El
                flujo normal de un cliente debe quedar gobernado por la
                suscripción de Mercado Pago y sus webhooks.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
